import {useContext} from "react";

import {toaster} from "../../../utilities/hooks/toaster";

import {TabsContext} from "./tabs.context";

export const useTabsContext = () => {
  const tabsConfig = useContext(TabsContext);

  if (!tabsConfig) {
    throw new Error("TabsContext must be used within a Tabs container (withTabs hok)");
  }

  const {
    addNewTab,
    addTabOnIndex,
    tabs,
    setTabs,
    currentTabIndex,
    closeTab,
    setCurrentTabIndex,
    closeByTabIdName,
    currentTabIdName,
  } = tabsConfig;

  const tabsCount = tabs?.length;

  function addNewTabCustom({
    TabComponent,
    controlled = true,
    title,
    headingNumber,
    tabIdName,
    onError = () => {},
    index, //* позиция, на которую добавить новую вкладку (по умолчанию в конце)
    ...props
  }) {
    const tabName = tabIdName || title;
    const tabIndex = tabs?.findIndex(tab => tab.tabIdName === tabName);

    if (tabIndex > 0) {
      toaster.warning("The tab already exists!");
      setCurrentTabIndex(tabIndex);
      onError();
      return;
    }

    if (index) {
      addTabOnIndex(
        {
          TabComponent,
          controlled,
          title: title,
          headingNumber: headingNumber,
          tabIdName: tabName,
          ...props,
        },
        index,
      );
    } else {
      addNewTab({
        TabComponent,
        controlled,
        title: title,
        headingNumber: headingNumber,
        tabIdName: tabName,
        ...props,
      });
    }

    setTimeout(() => {
      setCurrentTabIndex(index || tabsCount);
    }, 200);
  }

  function closeCurrentTab() {
    closeTab(currentTabIndex);
  }

  const collectIndexesToClose = (index, acc = []) => {
    const tab = tabs[index];
    if (tab?.children?.length) {
      for (const childIndex of tab.children) {
        collectIndexesToClose(childIndex, acc);
      }
    }
    acc.push(index);
    return acc;
  };

  const closeTabWrapper = index => {
    const tab = tabs[index];
    if (!tab?.children?.length) {
      return closeTab(index);
    }
    const indexesToClose = collectIndexesToClose(index);
    const uniqueIndexes = [...new Set(indexesToClose)];
    const removeSet = new Set(uniqueIndexes);

    const newTabs = tabs.filter((_, i) => !removeSet.has(i));
    setTabs(newTabs);

    const removedBeforeOrAtCurrent = uniqueIndexes.filter(i => i <= currentTabIndex).length;

    if (removedBeforeOrAtCurrent > 0) {
      setCurrentTabIndex(currentTabIndex - removedBeforeOrAtCurrent);
    }
  };

  function closeTabs(indexes) {
    if (!indexes || indexes?.length === 0) return;

    const newTabs = tabs.filter((tab, index) => !indexes.includes(index));
    setTabs(newTabs);
    setCurrentTabIndex(newTabs?.length - 1);
  }

  const changeCurrentTabName = (name, tabIdName) => {
    if (!name) return;

    setTabs(prev => {
      if (prev?.length <= currentTabIndex) return prev;
      let newTabs = [...prev];
      newTabs[currentTabIndex].title = name;
      if (newTabs[currentTabIndex]?.tabIdName)
        newTabs[currentTabIndex].tabIdName = tabIdName || newTabs[currentTabIndex].tabIdName;
      return newTabs;
    });
  };

  const setCouldTabClose = isClosable => {
    setTabs(prev => {
      if (prev?.length <= currentTabIndex) return prev;

      let newTabs = [...prev];
      newTabs[currentTabIndex].controlled = isClosable;

      return newTabs;
    });
  };

  return {
    addNewTab,
    tabsCount,
    currentTabIndex,
    closeTab: closeTabWrapper,
    tabs,
    setCurrentTabIndex,
    addNewTabCustom,
    closeCurrentTab,
    changeCurrentTabName,
    closeTabs,
    closeByTabIdName,
    setTabs,
    setCouldTabClose,
    currentTabIdName,
  };
};
